import { useQuery } from "@tanstack/react-query";

import { useStore } from "@nanostores/react";
import { useSession } from "@vega-api/custom/SessionContext/SessionContext";
import { getFeatureFlagValue } from "@vega-api/generated/featureflag-v1/featureflag-v1";
import {
  getFeatureFlagValueV2AfterAuth,
  getFeatureFlagValueV2PreAuth,
} from "@vega-api/generated/featureflag-v2/featureflag-v2";

import { FeatureFlag } from "./featureFlagEnum";
import {
  $featureFlagOverrides,
  applyFeatureFlagOverrides,
} from "./featureFlagOverrides";

export { FeatureFlag } from "./featureFlagEnum";

export const featureFlagKeys = Object.values(FeatureFlag);

export type FeatureFlagKeys = `${FeatureFlag}`;

export const defaultFeatureFlags = Object.fromEntries(
  featureFlagKeys.map((key) => [key, false]),
) as Record<FeatureFlagKeys, boolean>;

const FEATURE_FLAG_V2_KEY = "feature_flag_v2";

const CACHE_TIME = 1 * 60 * 1000;
const RETRY_ATTEMPTS = 2;
const RETRY_DELAY = 1000;

const FEATURE_FLAG_V2_CHECK_QUERY_KEY = ["featureFlagV2Check"];
const FEATURE_FLAG_QUERY_KEY = ["featureFlags"];
const FEATURE_FLAG_PRE_AUTH_QUERY_KEY = ["featureFlagPreAuth"];

const useIsV2Enabled = () => {
  const { data, status } = useQuery({
    queryKey: FEATURE_FLAG_V2_CHECK_QUERY_KEY,
    queryFn: ({ signal }) =>
      getFeatureFlagValue(
        { featureNames: [FEATURE_FLAG_V2_KEY] },
        { skipInterceptors: true },
        signal,
      ),
    staleTime: CACHE_TIME,
    retry: RETRY_ATTEMPTS,
    retryDelay: RETRY_DELAY,
  });

  return {
    isV2Enabled: data?.featureFlagValues[FEATURE_FLAG_V2_KEY] === true,
    isSettled: status === "success" || status === "error",
  };
};

export const useFeatureFlag = () => {
  const { isV2Enabled, isSettled } = useIsV2Enabled();
  const session = useSession();
  const userId = session.user?.id;

  const {
    data: featureFlags,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: isV2Enabled
      ? [...FEATURE_FLAG_QUERY_KEY, true]
      : [...FEATURE_FLAG_QUERY_KEY, false, userId],
    queryFn: ({ signal }) =>
      isV2Enabled
        ? getFeatureFlagValueV2AfterAuth(
            { featureNames: [...featureFlagKeys] },
            { skipInterceptorsWithToken: true },
            signal,
          )
        : getFeatureFlagValue(
            { userId, featureNames: [...featureFlagKeys] },
            { skipInterceptorsWithToken: true },
            signal,
          ),
    enabled: isSettled && userId !== undefined,
    staleTime: CACHE_TIME,
    retry: RETRY_ATTEMPTS,
    retryDelay: RETRY_DELAY,
  });

  const overrides = useStore($featureFlagOverrides);
  const flags = applyFeatureFlagOverrides(
    featureFlags?.featureFlagValues ?? defaultFeatureFlags,
    overrides,
  );

  return {
    featureFlag: flags,
    isLoading: !isSettled || isLoading,
    isError,
    error,
    refetch,
  };
};

/**
 * Reads a single feature flag before authentication, exposing its loading state
 * so callers can gate rendering until the value settles. Mirrors the post-auth
 * v1/v2 selection via {@link useIsV2Enabled}: when v2 is enabled the value comes
 * from the v2 pre-auth endpoint (allowlisted flags only), otherwise from v1.
 */
export const useNoUserFeatureFlagWithStatus = (
  key: FeatureFlagKeys,
): { isEnabled: boolean; isLoading: boolean } => {
  const { isV2Enabled, isSettled } = useIsV2Enabled();

  const {
    data: featureFlags,
    isLoading: isFlagLoading,
    isError,
  } = useQuery({
    queryKey: [...FEATURE_FLAG_PRE_AUTH_QUERY_KEY, isV2Enabled, key],
    queryFn: ({ signal }) =>
      isV2Enabled
        ? getFeatureFlagValueV2PreAuth(
            { featureNames: [key] },
            { skipInterceptors: true },
            signal,
          )
        : getFeatureFlagValue(
            { featureNames: [key] },
            { skipInterceptors: true },
            signal,
          ),
    enabled: isSettled,
    staleTime: CACHE_TIME,
    retry: RETRY_ATTEMPTS,
    retryDelay: RETRY_DELAY,
  });

  const isLoading = !isSettled || isFlagLoading;

  return {
    isEnabled:
      !isLoading && !isError && Boolean(featureFlags?.featureFlagValues[key]),
    isLoading,
  };
};

export const useNoUserFeatureFlag = (key: FeatureFlagKeys) => {
  const { isEnabled, isLoading } = useNoUserFeatureFlagWithStatus(key);

  return isLoading ? false : isEnabled;
};
